'use client';

import React, { useState, useEffect } from 'react';

interface CVRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  email: string;
  company: string;
  role: string;
  industry: string;
  interestType: string;
}

const INDUSTRIES = [
  { value: '', label: 'Select industry' },
  { value: 'SaaS', label: 'SaaS' },
  { value: 'E-commerce', label: 'E-commerce' },
  { value: 'Agency', label: 'Agency' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Healthcare', label: 'Healthcare' },
  { value: 'Startup', label: 'Startup' },
  { value: 'Other', label: 'Other' },
];

const INTEREST_TYPES = [
  { value: '', label: 'What are you interested in? (optional)' },
  { value: 'Hiring', label: 'Hiring' },
  { value: 'Freelance collaboration', label: 'Freelance collaboration' },
  { value: 'Just networking', label: 'Just networking' },
];

export default function CVRequestModal({ isOpen, onClose }: CVRequestModalProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    role: '',
    industry: '',
    interestType: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setFormData({ name: '', email: '', company: '', role: '', industry: '', interestType: '' });
      setIsSubmitted(false);
      setError('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/cv-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.error ?? 'Something went wrong. Please try again.');
      } else {
        setIsSubmitted(true);
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cv-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(14, 14, 18, 0.88)', backdropFilter: 'blur(6px)' }}
        onClick={onClose}
      />

      {/* Modal panel */}
      <div
        className="relative w-full max-w-lg rounded-lg overflow-hidden"
        style={{ background: '#1a1a22', border: '1px solid rgba(255,255,255,0.1)' }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-secondary-dark hover:text-light-warm transition-colors duration-200 z-10"
          aria-label="Close modal"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        <div className="px-8 py-8">
          {isSubmitted ? (
            /* ── Confirmation state ── */
            <div className="text-center py-6">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ background: 'rgba(6, 182, 212, 0.12)', border: '1px solid rgba(6,182,212,0.3)' }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="#06b6d4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h2 className="font-condensed font-bold text-2xl uppercase text-light-warm mb-3">
                Request Received!
              </h2>
              <p className="font-serif text-secondary-dark leading-relaxed" style={{ opacity: 0.8 }}>
                Thanks! Your request has been received. I&apos;ll review it and send my CV shortly.
              </p>
              <button
                onClick={onClose}
                className="mt-8 btn-primary rounded-br-[5px] rounded-t-[5px] rounded-bl-[5px]"
              >
                Close
              </button>
            </div>
          ) : (
            /* ── Form state ── */
            <>
              <div className="mb-6">
                <h2
                  id="cv-modal-title"
                  className="font-condensed font-bold text-2xl uppercase text-light-warm mb-1"
                >
                  Request My CV
                </h2>
                <p className="font-serif text-secondary-dark text-sm" style={{ opacity: 0.7 }}>
                  Share a few details and I&apos;ll send it over
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    className="w-full px-4 py-3 rounded font-sans text-sm text-light-warm placeholder-secondary-dark focus:outline-none transition-colors duration-200"
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(6,182,212,0.5)')}
                    onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)')}
                  />
                </div>

                {/* Email */}
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    className="w-full px-4 py-3 rounded font-sans text-sm text-light-warm placeholder-secondary-dark focus:outline-none transition-colors duration-200"
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(6,182,212,0.5)')}
                    onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)')}
                  />
                </div>

                {/* Company */}
                <div>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Company name"
                    required
                    className="w-full px-4 py-3 rounded font-sans text-sm text-light-warm placeholder-secondary-dark focus:outline-none transition-colors duration-200"
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(6,182,212,0.5)')}
                    onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)')}
                  />
                </div>

                {/* Role */}
                <div>
                  <input
                    type="text"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    placeholder="Your role"
                    required
                    className="w-full px-4 py-3 rounded font-sans text-sm text-light-warm placeholder-secondary-dark focus:outline-none transition-colors duration-200"
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(6,182,212,0.5)')}
                    onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)')}
                  />
                </div>

                {/* Industry */}
                <div>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded font-sans text-sm focus:outline-none transition-colors duration-200 appearance-none cursor-pointer"
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: formData.industry ? '#e8e0d0' : 'rgba(180,170,155,0.6)',
                    }}
                  >
                    {INDUSTRIES.map((opt) => (
                      <option
                        key={opt.value}
                        value={opt.value}
                        disabled={opt.value === ''}
                        style={{ background: '#1a1a22', color: '#e8e0d0' }}
                      >
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Interest Type (optional) */}
                <div>
                  <select
                    name="interestType"
                    value={formData.interestType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded font-sans text-sm focus:outline-none transition-colors duration-200 appearance-none cursor-pointer"
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: formData.interestType ? '#e8e0d0' : 'rgba(180,170,155,0.6)',
                    }}
                  >
                    {INTEREST_TYPES.map((opt) => (
                      <option
                        key={opt.value}
                        value={opt.value}
                        style={{ background: '#1a1a22', color: '#e8e0d0' }}
                      >
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {error && (
                  <p className="text-sm font-sans" style={{ color: '#f87171' }}>
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary rounded-br-[5px] rounded-t-[5px] rounded-bl-[5px] justify-center mt-2"
                  style={{ opacity: isSubmitting ? 0.7 : 1 }}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="31.4" strokeDashoffset="10" />
                      </svg>
                      Sending request…
                    </>
                  ) : (
                    <>
                      Send Request
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
